/**
 * Wave 7 / Lot B — formatage HONNÊTE des valeurs du portail client.
 *
 * Le portail affiche des données financières qui appartiennent au CLIENT de
 * l'artisan : factures, avoirs, tarifs, quantités livrées. Règles des Vagues 4-5,
 * appliquées ici mot pour mot :
 *
 *   — une valeur absente s'affiche « — », jamais « 0,00 € » ;
 *   — `NaN €` ne s'affiche jamais (une donnée illisible est une donnée absente) ;
 *   — une somme dont UN terme manque n'est pas une somme : elle vaut `null`.
 *
 * Un vrai zéro reste un zéro : un solde soldé s'écrit « 0,00 € », pas « — ».
 */

/** Marque universelle d'une valeur inconnue dans le portail. */
export const DASH = "—";

const EUR = new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" });
const NUM = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 3 });

/**
 * Convertit une valeur d'API en nombre exploitable, ou `null`. Les décimaux
 * Prisma arrivent sérialisés en chaîne : `"284.20"` est un nombre, `""` non.
 * `Number(null)` vaut 0 et `Number(undefined)` vaut NaN — les deux sont des
 * pièges, d'où le filtrage explicite avant toute conversion.
 */
export function toFiniteNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value !== "number" && typeof value !== "string") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** Montant en euros, ou « — » si le montant n'est pas connu. */
export function formatEuro(value: unknown): string {
  const n = toFiniteNumber(value);
  return n === null ? DASH : EUR.format(n);
}

/** Quantité (+ unité si elle est connue), ou « — ». */
export function formatQuantity(value: unknown, unit?: string | null): string {
  const n = toFiniteNumber(value);
  if (n === null) return DASH;
  return unit ? `${NUM.format(n)} ${unit}` : NUM.format(n);
}

/** Date au format jour français, ou « — » si elle est absente ou illisible. */
export function formatDay(value: string | null | undefined): string {
  if (!value) return DASH;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return DASH;
  return d.toLocaleDateString("fr-FR");
}

/**
 * Somme d'agrégat : `null` dès qu'un terme est inconnu. Une liste vide vaut 0
 * (rien à additionner n'est pas une donnée manquante — c'est un total nul).
 */
export function sumOrNull(values: readonly unknown[]): number | null {
  let total = 0;
  for (const v of values) {
    const n = toFiniteNumber(v);
    if (n === null) return null;
    total += n;
  }
  return total;
}
